"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import NextLink from "next/link";

import { CONCIERGE_WHATSAPP_HREF } from "@/lib/concierge-contact";
import { PRIVACY_HREF, TERMS_HREF } from "@/lib/legal/legal-document";

import { IMAGE_ASPECT_RATIOS, IMAGE_QUALITY, IMAGE_SIZES_PRESETS } from "@/lib/images/image-config";

interface InfoItem {
  id: string;
  numeral: string;
  title: string;
  /** Where the row leads — the reference's `_rowHref`. */
  href: string;
  /** The Concierge's WhatsApp opens in a new tab; the legal pages open here. */
  external: boolean;
  img: string;
}

/** Shown while no row is hovered. */
const RESTING_IMAGE = "/images/homepage/cena-de-repouso.jpg";

/**
 * The four subjects, as links — the reference's default `hyperlinks` variant (MVP-NOVA-LISTA,
 * `centralVariante`), which replaced the `accordion` approved on 2026-09-21 now that the two legal
 * pages exist. The rows keep the accordion's drawing; the plus becomes the arrow, and the answer
 * that used to open in place is the page the row leads to.
 */
const INFO_ITEMS: InfoItem[] = [
  {
    id: "termos",
    numeral: "I.",
    title: "Termos e Condições",
    href: TERMS_HREF,
    external: false,
    // The reference's own photographs for these rows (brand CDN, `MVP-NOVA-LISTA`).
    img: "https://static.taniabulhoes.com.br/6_0016a58020.jpg",
  },
  {
    id: "concierge",
    numeral: "II.",
    title: "Atendimento da Concierge",
    href: CONCIERGE_WHATSAPP_HREF,
    external: true,
    img: "https://static.taniabulhoes.com.br/5_fb2e74187d.jpg",
  },
  {
    id: "ajuda",
    numeral: "III.",
    title: "Precisa de Ajuda?",
    href: CONCIERGE_WHATSAPP_HREF,
    external: true,
    img: "https://static.taniabulhoes.com.br/6_0016a58020.jpg",
  },
  {
    id: "privacidade",
    numeral: "IV.",
    title: "Política de Privacidade",
    href: PRIVACY_HREF,
    external: false,
    img: "/images/homepage/cena-de-repouso-2-mobile.jpg",
  },
];

/** No row hovered. */
const NO_ACTIVE_ROW = -1;

/** The resting photograph's place in the stack: below every row. */
const RESTING_LAYER = NO_ACTIVE_ROW;

/** The reference's clip reveal: 0.68s on an out-expo-like curve (`applyImg`). */
const CLIP_TRANSITION = "clip-path .68s cubic-bezier(0.16,1,0.3,1)";

type LayerStyle = { clipPath: string; zIndex: number; transition: string };

/**
 * Where a photograph sits, given the row now shown (`active`) and the one before it
 * (`previous`) — the reference's `imgState`. The resting photograph is layer -1, below all.
 *
 * Of the two, the higher layer sits on top and is the one that moves: going down the list it
 * is the incoming photo, revealed bottom → top; going up it is the outgoing one, retreating
 * top → down to uncover the lower photo already beneath. Every other layer is clipped away.
 */
function layerStyle(layer: number, active: number, previous: number): LayerStyle {
  const high = Math.max(active, previous);
  const low = Math.min(active, previous);
  const shown = "inset(0 0 0 0)";
  const hidden = "inset(100% 0 0 0)";

  if (layer === high && layer === low) return { clipPath: shown, zIndex: 3, transition: "none" };
  if (layer === high) return { clipPath: high === active ? shown : hidden, zIndex: 3, transition: CLIP_TRANSITION };
  if (layer === low) return { clipPath: shown, zIndex: 2, transition: "none" };
  return { clipPath: hidden, zIndex: 1, transition: "none" };
}

/**
 * "Central de Informações" — the four subjects around a list, each row a link, while the
 * photo and the highlight bar follow the hovered row. Column ladder is in `.tb-info-grid`; the
 * order-* utilities reflow it to one column below 900px.
 */
export function InfoCenterSection() {
  // The row shown and the one before it: together they decide which photograph moves.
  const [{ activeIndex, previousIndex }, setRows] = useState({
    activeIndex: NO_ACTIVE_ROW,
    previousIndex: NO_ACTIVE_ROW,
  });
  const barRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  // The bar is one shared element that travels between rows, so hovering down
  // the list reads as a single sliding block instead of five separate fills.
  const moveBar = useCallback((index: number) => {
    const bar = barRef.current;
    const row = rowRefs.current[index];
    if (!bar || !row) return;
    bar.style.height = `${row.offsetHeight}px`;
    bar.style.transform = `translateY(${row.offsetTop}px) scaleY(1)`;
  }, []);

  // Keeps the current `translateY` and only collapses the scale, so the bar
  // shrinks in place instead of sliding back to the top of the list.
  const collapseBar = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.style.transform = bar.style.transform.replace("scaleY(1)", "scaleY(0)");
  }, []);

  const handleRowEnter = useCallback((index: number) => {
    setRows((rows) => ({ activeIndex: index, previousIndex: rows.activeIndex }));
    moveBar(index);
  }, [moveBar]);

  // The resting photograph comes back by the same rule: it is the lowest layer, so the row
  // photo on top retreats to uncover it.
  const handleRowLeave = useCallback(() => {
    setRows((rows) => ({ activeIndex: NO_ACTIVE_ROW, previousIndex: rows.activeIndex }));
    collapseBar();
  }, [collapseBar]);

  // Focus moving to another row keeps the bar up, for the same reason
  // `onMouseLeave` sits on the block: otherwise it collapses and re-grows.
  const handleBlockBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    handleRowLeave();
  }, [handleRowLeave]);

  return (
    <section
      id="ajuda"
      className="bg-[var(--tb-porcelain-tint)] pt-[clamp(64px,9vh,124px)] pb-[clamp(70px,10vh,130px)]"
    >
      <div className="tb-info-grid">
        {/* Left column: eyebrow at the top, photo at the bottom. */}
        <div className="contents [@media(min-width:900px)]:flex [@media(min-width:900px)]:flex-col [@media(min-width:900px)]:justify-between [@media(min-width:900px)]:gap-[clamp(40px,6vw,90px)]">
          <span className="order-1 text-[15px] font-medium uppercase tracking-[0.16em] text-tb-green [@media(min-width:900px)]:order-none">
            Central de Informações
          </span>

          {/*
            All six photos are stacked; `clip-path` produces the directional wipe.
            Three layers at any moment: the entering row on top (animating), the row
            it replaces underneath, and the resting photo at the bottom.
          */}
          <div
            className="order-4 relative w-full overflow-hidden rounded-[2px] bg-[var(--tb-porcelain-deep,#ECE2D8)] transition-[filter] duration-[360ms] ease-[var(--tb-ease-linen)] hover:brightness-[0.78] [@media(min-width:900px)]:order-none [@media(min-width:900px)]:w-[min(100%,320px)]"
            style={{ aspectRatio: IMAGE_ASPECT_RATIOS.editorialPortrait }}
          >
            {/*
              One crop here, unlike the hero: this slot is a portrait box at every width, and the
              reference's two crops of this photograph are both portrait.
            */}
            <Image
              src={RESTING_IMAGE}
              alt="Atendimento Tania Bulhões"
              fill
              sizes={IMAGE_SIZES_PRESETS.sidebar}
              quality={IMAGE_QUALITY.default}
              className="object-cover object-center"
              style={layerStyle(RESTING_LAYER, activeIndex, previousIndex)}
            />
            {INFO_ITEMS.map((item, index) => {
              return (
                <Image
                  key={item.id}
                  src={item.img}
                  alt={item.title}
                  fill
                  sizes={IMAGE_SIZES_PRESETS.sidebar}
                  quality={IMAGE_QUALITY.default}
                  className="object-cover object-center"
                  style={layerStyle(index, activeIndex, previousIndex)}
                />
              );
            })}
          </div>
        </div>

        {/* Right column: heading at the top, link rows at the bottom. */}
        <div className="contents [@media(min-width:900px)]:flex [@media(min-width:900px)]:flex-col [@media(min-width:900px)]:justify-between [@media(min-width:900px)]:gap-[clamp(48px,5vw,64px)]">
          <p className="tb-reveal-view order-2 font-display text-[clamp(24px,2.8vw,40px)] font-light leading-[1.3] tracking-[-0.01em] text-[var(--fg)] [text-wrap:pretty] [@media(min-width:900px)]:order-none">
            Estamos por perto em cada etapa. Reunimos aqui tudo o que envolve a sua lista, do primeiro cadastro à entrega do presente, para que cada gesto seja simples, seguro e{" "}
            <span className="text-tb-green">memorável</span>.
          </p>

          {/* `onMouseLeave` sits on the block, not on each row: moving between
              adjacent rows would otherwise collapse and re-grow the bar. */}
          <div
            className="order-3 relative flex flex-none flex-col [@media(min-width:900px)]:order-none"
            onMouseLeave={handleRowLeave}
            onBlur={handleBlockBlur}
          >
            {/* Highlight bar — positioned and sized by `moveBar`.
                Brand green, the footer band's own fill (`newsletter-footer-band.tsx`):
                ruling of 2026-10-06, "a cor do hover igual a cor do footer". It replaces
                oxide, which the design reference approximated with a raw #404731 that is
                not a DS token (see docs/internal/ds-variant-candidates.md § Tokens). The
                row it covers keeps flipping to porcelain, the light ink the footer pairs
                with the same green — contrast noted in
                docs/internal/fidelidade/desvios-aprovados.md. */}
            <div
              ref={barRef}
              className="pointer-events-none absolute left-0 right-0 top-0 z-[1] origin-top will-change-transform"
              style={{
                height: 0,
                background: "var(--tb-green)",
                transform: "translateY(0px) scaleY(0)",
                transition: "transform .42s var(--tb-ease-linen)",
              }}
            />

            {INFO_ITEMS.map((item, index) => {
              const rowClassName =
                "relative z-[2] grid w-full cursor-pointer grid-cols-[24%_minmax(0,1fr)_auto] items-center gap-x-[clamp(16px,2vw,30px)] py-[clamp(18px,3.3%,46px)] pl-[13px] pr-[20px] text-left font-sans transition-colors duration-[380ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2 [@media(min-width:900px)]:grid-cols-[33%_minmax(0,1fr)_auto]";
              const rowProps = {
                onMouseEnter: () => handleRowEnter(index),
                onFocus: () => handleRowEnter(index),
                className: rowClassName,
                // Flips to porcelain once the bar is behind the row.
                style: { color: activeIndex === index ? "var(--tb-porcelain)" : "var(--fg)" },
              };
              const content = (
                <>
                  {/* `leading-[normal]`, not the 1.5 base: the reference relies on the
                      font's own metrics here, which is what sets the row height. */}
                  <span className="relative z-[2] text-[clamp(15px,1.2vw,17px)] leading-[normal]">{item.numeral}</span>
                  <span className="relative z-[2] text-[clamp(15px,1.2vw,17px)] leading-[normal]">{item.title}</span>
                  {/* The reference's arrow for the hyperlink rows, in the 19px/1 cell the plus
                      used, so the row keeps its height. */}
                  <span aria-hidden="true" className="relative z-[2] justify-self-end text-[19px] leading-none">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mt-0.5 inline-flex"
                    >
                      <path d="M7 17 17 7" />
                      <path d="M8 7h9v9" />
                    </svg>
                  </span>
                </>
              );

              return (
                <div
                  key={item.id}
                  // Row element, not the link: `moveBar` reads `offsetTop` and
                  // `offsetHeight` from the element the bar is positioned against.
                  ref={(element) => { rowRefs.current[index] = element; }}
                  className={
                    index === INFO_ITEMS.length - 1
                      ? "border-y border-[rgba(61,63,57,0.15)]"
                      : "border-t border-[rgba(61,63,57,0.15)]"
                  }
                >
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" {...rowProps}>
                      {content}
                    </a>
                  ) : (
                    <NextLink href={item.href} {...rowProps}>
                      {content}
                    </NextLink>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
